import clsx from 'clsx';
import React, {useRef, useState} from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {ButtonBase, Menu, MenuItem} from '@mui/material';
import {Body, designSystemToken} from '@lightricks/react-design-system';
import {ContentSubmissionVersion} from '@/types/contentSubmission';
import styles from './ContentSubmissionVersionSelector.module.scss';

export interface ContentSubmissionVersionSelectorProps {
  testID?: string;
  versions: ContentSubmissionVersion[];
  activeVersionId: string | null;
  onVersionChange: (versionId: string) => void;
  contentTypeLabel?: string;
}

function ContentSubmissionVersionSelector({
  testID = 'content-submission-version-selector',
  versions,
  activeVersionId,
  onVersionChange,
  contentTypeLabel = '',
}: ContentSubmissionVersionSelectorProps) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  // API returns versions newest-first, so version number = length - index
  const activeIndex = versions.findIndex((v) => v.id === activeVersionId);
  const activeVersionNumber =
    activeIndex === -1 ? versions.length : versions.length - activeIndex;
  const label = contentTypeLabel
    ? `${contentTypeLabel} V${activeVersionNumber}`
    : `V${activeVersionNumber}`;

  // Pre-compute id → version number (newest-first array, so V1 = last element)
  const versionNumberMap = Object.fromEntries(
    versions.map((v, i) => [v.id, versions.length - i])
  );

  // Display newest-first in the menu
  const reversedVersions = [...versions].reverse();

  if (versions.length <= 1) {
    return (
      <Body size="lg" color={designSystemToken('semantic.fg.secondary')}>
        {label}
      </Body>
    );
  }

  return (
    <>
      <ButtonBase
        ref={anchorRef}
        data-testid={testID}
        className={styles.trigger}
        onClick={() => setOpen(true)}
      >
        <Body size="lg" color={designSystemToken('semantic.fg.secondary')}>
          {label}
        </Body>
        <ExpandMoreIcon className={styles.caret} />
      </ButtonBase>
      <Menu
        anchorEl={anchorRef.current}
        open={open}
        onClose={() => setOpen(false)}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}
        MenuListProps={{className: styles.menuList}}
      >
        {reversedVersions.map((version) => {
          const versionNumber = versionNumberMap[version.id];
          const itemLabel = contentTypeLabel
            ? `${contentTypeLabel} V${versionNumber}`
            : `V${versionNumber}`;
          const isSelected = version.id === activeVersionId;
          return (
            <MenuItem
              key={version.id}
              className={styles.thumbnailItem}
              onClick={() => {
                onVersionChange(version.id);
                setOpen(false);
              }}
            >
              <div
                className={clsx(styles.thumbnail, {
                  [styles.selected]: isSelected,
                })}
                style={{backgroundImage: `url(${version.thumbnailUrl})`}}
              />
              <Body size="sm" color={designSystemToken('semantic.fg.primary')}>
                {itemLabel}
              </Body>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

export default ContentSubmissionVersionSelector;
